import clsx from 'clsx';
import {
  type ChangeEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';

import { CheckmarkIcon, XmarkIcon } from '@dynatrace/strato-icons';

import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { useAriaLabelingProps as _useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { fieldTokens as fieldColorsCSS } from '../../styles/field.sty.js';
import { useInternalFormRegistration } from '../form/hooks/useInternalFormRegistration.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormControl } from '../forms-core/hooks/useFormControl.js';
import {
  getI18nValidationState,
  useFormFieldValidation,
} from '../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useContainerFocus } from '../hooks/useContainerFocus.js';
import type { FormControlRef } from '../shared-types.js';
import type { MessageTarget } from '../validation/message-routing.js';
import type { ValidationState } from '../validation/types.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';
import {
  switchIconCSS,
  switchLabelCSS,
  switchTextCSS,
  switchThumbCSS,
  switchWrapperCSS,
} from './Switch.sty.js';

function getSwitchFieldVariant(isSelected: boolean, showError: boolean) {
  if (isSelected) {
    return 'accent';
  }
  if (showError) {
    return 'emphasized';
  }
  return 'default';
}

const COMPONENT_NAME = 'Switch';

// #region Types
/**
 * @public
 */
export type SwitchProps = FormControlProps<
  boolean,
  (checked: boolean, event?: ChangeEvent<HTMLInputElement>) => void | never,
  HTMLDivElement
> &
  StylingProps &
  DataTestId &
  MaskingProps &
  BehaviorTrackingProps &
  WithChildren & {
    /**
     * Internal: associates this control with a `_Form` rendered outside its
     * React subtree via the native `form` attribute. Unstable.
     * @internal
     */
    _form?: string;
    /**
     * Internal: routes this control's native/Strato validation messages to the
     * field outlet (default), the enclosing form's outlet, or both. Unstable.
     * @internal
     */
    _messageTarget?: MessageTarget | MessageTarget[];
    /** The string used as the value of the switch when submitting it in a form, if the switch is toggled on. */
    formValue?: string;
  };
// #endregion

/**
 * Use the `Switch` to let users toggle a setting between on and off. The change takes effect immediately without requiring a separate confirmation step.
 * @public
 */
export const Switch = forwardRef<FormControlRef, SwitchProps>(
  (props, forwardedRef) => {
    const {
      value,
      defaultValue,
      children,
      // oxlint-disable-next-line typescript/no-deprecated -- APPDEV-18321
      controlState,
      name,
      _form,
      _messageTarget,
      formValue,
      onValidityChange,
      onChange,
      onFocus,
      onBlur,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      id: propId,
      required: propRequired,
      disabled: propDisabled,
      'aria-disabled': ariaDisabledProp,
      readOnly: propReadOnly,
      ...remainingProps
    }: SwitchProps = props;

    const [behavioralTrackingProps] =
      useBehavioralTrackingProps(remainingProps);

    const {
      id,
      disabled,
      required,
      ariaDisabled,
      readOnly,
      inputProps,
      hintProps,
      hint,
      showError,
      messageDispatch: messagesContextDispatch,
    } = useFormControl({
      propId,
      propDisabled,
      propRequired,
      propAriaDisabled: ariaDisabledProp,
      propReadOnly,
      controlState,
    });

    const disabledOrAriaDisabled = disabled || ariaDisabled || false;

    const ariaLabelingProps = _useAriaLabelingProps(props);

    const [isSelected, setIsSelected] = useControllableState({
      value,
      defaultValue,
    });

    // Imperative Handle Construction
    const intl = useIntl();
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const controlRef = useRef<FormControlRef>(null);
    const combinedRef = useMergeRefs<FormControlRef | null>([
      forwardedRef,
      controlRef,
    ]);
    const formValidityChangeRef = useRef<
      ((state: ValidationState) => void) | undefined
    >(undefined);
    const _emitValidationState = useValidationStateEmitter(
      controlState
        ? undefined
        : (state) => formValidityChangeRef.current?.(state),
    );
    const emitValidationState = useCustomMessageEmitter(
      _emitValidationState,
      () => getI18nValidationState(intl, inputRef.current, 'manual'),
    );

    const formControl = useInternalFormRegistration({
      id,
      name,
      form: _form,
      messageTarget: _messageTarget,
      value: isSelected === true,
      controlRef,
      onValidityChange,
    });
    formValidityChangeRef.current = formControl.onValidityChange;

    useImperativeHandle(combinedRef, (): FormControlRef<HTMLDivElement> => {
      return {
        element: rootRef.current,
        inputRef: inputRef.current,
        focus() {
          inputRef.current?.focus();
        },
        validate() {
          inputRef.current?.checkValidity();
          messagesContextDispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
          emitValidationState(
            getI18nValidationState(intl, inputRef.current, 'manual'),
          );
        },
        // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
        validateAsync: async () => {
          inputRef.current?.checkValidity();
          messagesContextDispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
          return emitValidationState(
            getI18nValidationState(intl, inputRef.current, 'manual'),
          );
        },
      };
    });

    const {
      onChange: validationOnChange,
      onInvalid: validationOnInvalid,
      ariaProps,
    } = useFormFieldValidation(
      inputRef,
      { required, value: String(value), isSelected },
      undefined,
      undefined,
      _emitValidationState,
      controlState,
    );

    if (value !== undefined && !onChange && isDev()) {
      // oxlint-disable-next-line no-console
      console.log(
        'Defining "value" makes the switch controlled. Set the onChange() callback and implement the toggle functionality.',
      );
    }

    const { containerClassName, ...containerProps } =
      useContainerFocus<HTMLDivElement>({
        onFocus,
        onBlur,
        disabled,
        emitValidationState,
      });

    const color = isSelected ? 'primary' : 'neutral';
    let focusRingColor: 'critical' | 'primary' | 'neutral' = 'neutral';

    if (showError) {
      focusRingColor = 'critical';
    } else if (!(readOnly || disabledOrAriaDisabled) && isSelected) {
      focusRingColor = 'primary';
    }

    const { focusClassName, focusProps, isFocusVisible } = useFocusRing({
      color: focusRingColor,
      disabled,
    });

    function handleChange(e?: ChangeEvent<HTMLInputElement>): void {
      if (!disabled && !readOnly) {
        onChange?.(!isSelected, e);
        setIsSelected(!isSelected);
        validationOnChange();
      }
    }

    return (
      <Flex
        ref={rootRef}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        role="group"
        flexDirection="column"
        gap={4}
        className={clsx(containerClassName, consumerClassName)}
        style={consumerStyle}
        {...containerProps}
        {...behavioralTrackingProps}
        data-dt-component={COMPONENT_NAME}
      >
        <label
          htmlFor={id}
          className={clsx(
            switchLabelCSS({ disabled: disabledOrAriaDisabled, readOnly }),
          )}
        >
          <VisuallyHidden>
            <input
              id={id}
              type="checkbox"
              role="switch"
              checked={isSelected}
              aria-disabled={ariaDisabled}
              disabled={disabled}
              required={required}
              readOnly={readOnly}
              aria-checked={isSelected}
              onChange={handleChange}
              onBlur={focusProps.onBlur}
              onFocus={focusProps.onFocus}
              onInvalid={validationOnInvalid}
              name={name}
              form={_form}
              value={formValue}
              ref={inputRef}
              {..._mergeProps(
                controlState ? inputProps : ariaProps,
                ariaLabelingProps,
              )}
            />
          </VisuallyHidden>
          <div
            className={clsx(
              focusClassName,
              switchWrapperCSS({
                disabled: disabledOrAriaDisabled,
                color,
                hasError: showError,
                isFocusVisible,
                readOnly,
              }),
              fieldColorsCSS({
                disabled: disabledOrAriaDisabled,
                color: showError ? 'critical' : color,
                variant: getSwitchFieldVariant(isSelected, showError),
              }),
            )}
          >
            <div
              className={switchIconCSS({
                selected: isSelected,
                readOnly,
                hasError: showError,
              })}
            >
              <CheckmarkIcon size="small" />
              <div className={switchThumbCSS} />
              <XmarkIcon size="small" />
            </div>
          </div>
          <span
            className={switchTextCSS({
              disabled: disabledOrAriaDisabled,
              required: !!children && required,
            })}
          >
            {children}
          </span>
        </label>
        {hint && (
          <BaseItem
            variant={showError ? 'error' : 'hint'}
            disabled={disabledOrAriaDisabled}
            {...hintProps}
          >
            {hint}
          </BaseItem>
        )}
      </Flex>
    );
  },
);
(Switch as typeof Switch & { displayName: string }).displayName =
  COMPONENT_NAME;
